/**
 * Отметки выхода на смену.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ЭТО НЕ ОЧЕРЕДЬ ОТПРАВКИ, И ЭТО ГЛАВНОЕ РЕШЕНИЕ ФАЙЛА
 * ─────────────────────────────────────────────────────────────────────────
 * Ручки под журнал смен на бэкенде НЕТ (см. таблицу разрывов в CLAUDE.md,
 * строка 12). Класть отметку в `offline/outbox.ts` было бы неправильно
 * дважды, и оба раза плохо:
 *
 * 1. `POST` на несуществующий путь вернёт 404 → `isClient` → «отказ», и
 *    скаут увидит красное «Не удалось…» на действии, которое на самом деле
 *    сохранилось.
 * 2. Если вместо этого считать 404 поводом для повтора, недоставляемая
 *    запись встанет В ГОЛОВУ строго последовательной очереди и заблокирует
 *    «взять» и «завершить» за собой. Очередь сознательно не переставляет
 *    записи местами — это её свойство, а не недостаток.
 *
 * `enqueue()` означает «это доедет до сервера». У отметки такого обещания
 * сегодня нет, и врать очереди нельзя.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ПОЧЕМУ У ОТМЕТКИ УЖЕ ЕСТЬ id
 * ─────────────────────────────────────────────────────────────────────────
 * Это будущий X-Idempotency-Key, заведённый заранее. Отметки, записанные за
 * месяцы до появления ручки, надо будет досдать — и ровно по одному разу.
 * Ключ, придуманный в момент досылки, эту гарантию не даёт: то же правило,
 * по которому очередь генерирует id при постановке, а не при отправке.
 *
 * Когда ручка появится, переход механический: `addMark` дополнительно зовёт
 * `enqueue('shift_mark', mark.id, …)`, досылка проставляет `synced`. Ни
 * подпись `enqueue`, ни форма очереди не меняются.
 *
 * Что нужно на бэкенде (владелец — сервис `workforce`, docs/PLAN.md §3
 * Фаза 3; `fleet` не владелец: расписания — не данные о транспорте):
 *   POST /v1/scout/shift-marks  {shiftId, kind, at, lat, lng}
 *   гейт RequireRole(ctx, "CreateShiftMark", "scout") — тир 2, по роли:
 *   отметка про человека, а не про байк.
 * ⛔ Заблокировано миграцией: `atlas` не установлен (PLAN, риск 11).
 */
import { del, get, set } from 'idb-keyval'
import { newId } from './offline/outbox'
import { currentShift, nextShift, type ShiftId } from './shifts'

const SHIFT_LOG_KEY = 'aida.scout.shiftlog'

/**
 * Смена не закрыта дольше этого — считаем, что скаут забыл нажать «Закончить».
 * Самая длинная смена — восемь часов; четырнадцать заведомо больше любой.
 * Автоматически НЕ закрываем: придумать за человека время окончания хуже,
 * чем показать, что оно неизвестно.
 */
const STALE_AFTER_MS = 14 * 60 * 60 * 1000

export type ShiftMark = {
  /** UUID. Он же будущий X-Idempotency-Key — см. шапку. */
  id: string
  shiftId: ShiftId
  kind: 'in' | 'out'
  /** RFC3339 UTC: когда скаут нажал, а не когда мы это дошлём. */
  at: string
  /** Фактическая точка. (0,0) — «неизвестно», как везде (см. geo.hasPosition). */
  lat: number
  lng: number
  /** Отмечено вне окна смены. Не запрет, а факт для диспетчера. */
  outsideWindow: boolean
  /** Дошло ли до сервера. Пока ручки нет — всегда false. */
  synced: boolean
}

/** Чтение никогда не бросает: сбой хранилища не должен гасить экран. */
export async function readMarks(): Promise<ShiftMark[]> {
  try {
    return (await get<ShiftMark[]>(SHIFT_LOG_KEY)) ?? []
  } catch (e) {
    console.error('[shift-log] хранилище недоступно для чтения', e)
    return []
  }
}

/**
 * Запись БРОСАЕТ при сбое — то же различение, что в очереди: не прочитали —
 * покажем пусто; не записали — отметки не существует, и молчать нельзя.
 */
async function writeMarks(marks: ShiftMark[]): Promise<void> {
  try {
    if (marks.length === 0) {
      await del(SHIFT_LOG_KEY)
      return
    }
    await set(SHIFT_LOG_KEY, marks)
  } catch (e) {
    throw new Error('Не удалось сохранить отметку на устройстве', { cause: e })
  }
}

/**
 * Поставить отметку.
 *
 * Отметиться вне окна смены МОЖНО: скаут вправе выйти раньше или задержаться,
 * а часы, которыми приложение не управляет, — плохое основание для запрета.
 * Такая отметка помечается `outsideWindow`, и смена берётся ближайшая.
 */
export async function addMark(
  kind: 'in' | 'out',
  now: Date,
  at: { lat: number; lng: number } | null,
): Promise<ShiftMark> {
  const running = currentShift(now)
  const mark: ShiftMark = {
    id: newId(),
    shiftId: running?.id ?? nextShift(now).shift.id,
    kind,
    at: now.toISOString(),
    lat: at?.lat ?? 0,
    lng: at?.lng ?? 0,
    outsideWindow: running === null,
    synced: false,
  }
  const marks = await readMarks()
  marks.push(mark)
  await writeMarks(marks)
  return mark
}

export function lastMark(marks: readonly ShiftMark[]): ShiftMark | undefined {
  return marks[marks.length - 1]
}

/**
 * Считается ли скаут вышедшим на смену.
 *
 * Забытая открытой отметка НЕ делает человека «на смене» бесконечно: через
 * STALE_AFTER_MS она перестаёт учитываться, и экран показывает это отдельной
 * строкой, а не тихо переключается.
 */
export function isOnShift(marks: readonly ShiftMark[], now: Date): boolean {
  const last = lastMark(marks)
  if (!last || last.kind !== 'in') return false
  return now.getTime() - Date.parse(last.at) < STALE_AFTER_MS
}

/** Смена открыта, но слишком давно — скорее всего, забыли закрыть. */
export function staleOpenMark(
  marks: readonly ShiftMark[],
  now: Date,
): ShiftMark | null {
  const last = lastMark(marks)
  if (!last || last.kind !== 'in') return null
  return now.getTime() - Date.parse(last.at) >= STALE_AFTER_MS ? last : null
}

/**
 * Стереть журнал.
 *
 * ⚠ Обязано вызываться при выходе, рядом с clearOutbox(): телефоны у скаутов
 * сменные, и чужие отметки на них оставаться не должны.
 */
export async function clearMarks(): Promise<void> {
  try {
    await del(SHIFT_LOG_KEY)
  } catch (e) {
    console.error('[shift-log] не удалось стереть журнал', e)
  }
}
